import { LightningElement, track, api  } from 'lwc';

import {iconPath, publicPropertyToBool} from '../../../utils/basicUtils.js';
import { getLabelsByLanguage } from '../../../utils/languageUtil';
import {MATERIAL_ICONS} from '../../../utils/iconMapper.js';

import {handleDropdownFlip, suppressEscape} from '../../../utils/comboboxUtils.js';

/* Example Usage

    1.) Options with Metas:
        Javascript:
            myOptions = [
                {label: label1, value: value1, metaOne: 'extra info 1', metaTwo: 'extra info 2'},
                {label: label2, value: value2, metaOne: 'extra info 3', metaTwo: 'extra info 4'},
            ];

            // Handle response from <x-slds-combobox-search>
            myEventHandler(event) {
                event.stopPropagation();
                const response = event.detail;
            }


        HTML:
            // Example with an extra wide combobox dropdown
            <div slot="controls" class="slds-page-header__control slds-grid slds-m-right_medium" style="width: 30rem;">
                <div class="slds-grow">
                    <x-slds-combobox-search
                        options={myOptions}
                        label-top="Filers"              <-- Label above combobox
                        is-grow="true"                  <-- set is-grow to true for wide dropdown (need div wrapper: <div class="slds-col" style="width: <x>rem;">)
                        place-holder="My custom placeholder..."
                        onchange={myEventHandler}
                    ></x-slds-combobox-search>
                </div>
            </div>

            // Example with Dropdown that renders above the  combobox instead of below
            <x-slds-combobox-search
                is-dropdown-up="true"
                options={myOptions}
                place-holder="Search Placeholder..."
            ></x-slds-combobox-search>

*/

function materialIconConfig(iconName, isInputIcon=false) {
    const {path = null, viewBox = null} = MATERIAL_ICONS[iconName] || {};
    if (!path || !viewBox) {
        return null;
    }

    const inputAddon = isInputIcon ? ' slds-input__icon slds-input__icon_left' : '';

    return {
        container: `slds-icon_container material-container slds-line-height_reset slds-current-color${inputAddon}`,
        path,
        viewBox,
        isMaterialIcon: true
    };
}

function comboboxIconPath(icon, isInputIcon=false) {
    if (!icon || typeof icon === 'object') return icon;

    const [category, iconName] = icon.split(':');
    if (category?.trim() === 'material') {
        return materialIconConfig(iconName?.trim(), isInputIcon);
    }

    return iconPath(icon, true, isInputIcon);
}

function requiredPropertyToBool(val) {
    return val === true || val === '' || val === 'true' || val === '1';
}

const META_KEYS = ['metaOne', 'metaTwo', 'metaThree', 'metaFour'];


function handleOptionProps(option, hasMeta=false) {
    const hasIcon = comboboxIconPath(option.icon);

    const icon = hasIcon 
        ? comboboxIconPath(option.icon)
        : null;

    const isMeta = META_KEYS.some(mk => option?.[mk]);

    const optionClass = (isMeta && hasMeta) 
        ? 'slds-media slds-listbox__option slds-listbox__option_entity slds-listbox__option_has-meta'
        : hasIcon 
            ? 'slds-media slds-media_center slds-listbox__option slds-listbox__option_entity slds-listbox__option_plain'
        : 'slds-media slds-media_center slds-listbox__option slds-listbox__option_entity slds-listbox__option_plain'

    return {icon, isMeta, optionClass};
}

function handleErrorIconOnFormCl(formCl, hasError, suppressErrorText, originalFormCl) {
    if (!suppressErrorText || !hasError) {
        return originalFormCl;
    }

    let newFormCl = formCl;

    if (!this._formElementCl.includes('slds-input-has-icon')) {
        newFormCl += ' slds-input-has-icon';
    }
    if (!formCl.includes('slds-input-has-icon_left')) {
        if (formCl.includes('slds-input-has-icon_right')) {
            newFormCl.replace('slds-input-has-icon_right', 'slds-input-has-icon_left-right');
        } else {
            newFormCl += ' slds-input-has-icon_left';
        }
    }

    return newFormCl;
}

export default class sldsComboboxSearch extends LightningElement {
    @api 
    set unsearchableValue(val) {
        this._unsearchableValue = val;
        if (val) {
            this._selectedOption = val;
        }
    }
    get unsearchableValue() {
        return this._unsearchableValue;
    }

    _isRequired = false;
    @api
    set isRequired(val) {
        this._isRequired = requiredPropertyToBool(val);
    }
    get isRequired() {
        return this._isRequired;
    }

    @api
    set required(val) {
        this.isRequired = val;
    }
    get required() {
        return this.isRequired;
    }

    idCnt = 300;
    // --------- Tracked HTML Elements --------- \\
    _dropdownTrigger; // Controls open/close state
    _comboOptions;   // List of menu options
    _inputEle;       // <input> element
    _headerOption;
    // ----------------------------------------- \\

    _hasMeta = false;
    isRendered = false;
    @api dropdownDirection;
    @api suppressFlip = false;

    isEvent = false;
    _value = {label: '', value: ''};

    // ======================= Public Properties ========================= \\
    @api
    set optionsBordered(val) {
        this._optionsBordered = val;
        if (val) {
            this._applyVarsDirect('--option-border', '1px solid var(--slds-g-color-border-base-1)');
            this._applyVarsDirect('--header-option-border', '1px solid var(--slds-g-color-border-base-4)');
            this._applyVarsDirect('--option-padding', 'var(--slds-g-spacing-2, 0.25rem)');
            this._applyVarsDirect('--option-radius', '0px');
        }
    }
    get optionsBordered() {
        return this._optionsBordered;
    }
    @api 
    set optionsRadius(val) {
        this._optionsRadius = val;
        if (val) {
            this._applyVarsDirect('--option-radius', val);
        }
    }
    get optionsRadius() {
        return this._optionsRadius;
    }


    // --------------- Placeholder ----------------- \\
    // placeholder of input element
    @api
    set placeHolder(val) {
        this._placeHolder = getLabelsByLanguage(val) ?? val;
    }
    get placeHolder() {
        return this._placeHolder ?? `${getLabelsByLanguage('search')}...`;
    }

    _formElementCl = 'slds-combobox__form-element slds-input-has-icon slds-input-has-icon_left combobox-search';
    @api
    set suppressIcon(val) {
        this._suppressIcon = val;
        if (val) {
            this._formElementCl = 'slds-combobox__form-element combobox-search';
        }
    }
    get suppressIcon() {
        return this._suppressIcon;
    }

    @api
    set suppressBorder(val) {
        this._suppressBorder = val;
        if (val) {
            this._applyVarsDirect('--input-border', 'none');
        }
    }
    get suppressBorder() {
        return this._suppressBorder ?? false;
    }

    @api
    set disableLocalFilter(val) {this._disableLocalFilter = publicPropertyToBool(val);}
    get disableLocalFilter() {return this._disableLocalFilter;}

    @api
    set suppressSelectedIcon(val) {this._suppressSelectedIcon = requiredPropertyToBool(val);}
    get suppressSelectedIcon() {return this._suppressSelectedIcon ?? false;}

    _formControlClass = 'slds-form-element__control';
    @api
    set isSelectionVariant(val) {
        this._isSelectionVariant = publicPropertyToBool(val);
        if (val) {
            this._formControlClass = 'slds-form-element__control help-form';
            this.applyForcedSelection();
        }
    }
    get isSelectionVariant() {return this._isSelectionVariant;}

    @api
    set forcedSelection(val) {
        const wasForcedSelectionSelected = this.isForcedSelectionSelected();
        this._forcedSelection = this.normalizeSelection(val);

        if (!this._forcedSelection) {
            if (wasForcedSelectionSelected) {
                this.clearSelectionState();
            }
            return;
        }

        this.applyForcedSelection();
    }
    get forcedSelection() {
        return this.isSelectionVariant ? this._forcedSelection : null;
    }

    @api
    set inputHeight(val) {
        this._inputHeight = val;
        if (val) {
            if (val.includes('rem')) {
                const numVal = Number(val.replace('rem', ''));
                if (!isNaN(numVal) && isFinite(numVal) && numVal > 2.25) {
                    this._applyVarsDirect('--clear-button-transform', 'translateX(-5%) scale(1.2)');   
                }
            }
            this._applyVarsDirect('--input-height', val);
        }
    }
    get inputHeight() {
        return this._inputHeight;
    }
    @api
    set borderRadius(val) {
        this._borderRadius = val;
        if (val) {
            this._applyVarsDirect('--input-radius', val);
            this._applyVarsDirect('--slds-c-input-radius-border', val);
        }
    }
    get borderRadius() {
        return this._borderRadius;
    }
    @api
    set fontSize(val) {
        this._fontSize = val;
        if (val) this._applyVarsDirect('--input-font-size', val);

    }
    get fontSize() {
        return this._fontSize;
    }




    @api
    set topLeftRadius(val) {
        this._topLeftRadius = val;
        if (val) {
            this._applyVarsDirect('--input-radius-top-left', val);
        }
    }
    get topLeftRadius() {
        return this._topLeftRadius;
    }
    @api
    set topRightRadius(val) {
        this._topRightRadius = val;
        if (val) {
            this._applyVarsDirect('--input-radius-top-right', val);
        }
    }
    get topRightRadius() {
        return this._topRightRadius;
    }
    @api
    set bottomRightRadius(val) {
        this._bottomRightRadius = val;
        if (val) {
            this._applyVarsDirect('--input-radius-bottom-right', val);
        }
    }
    get bottomRightRadius() {
        return this._bottomRightRadius;
    }
    @api
    set bottomLeftRadius(val) {
        this._bottomLeftRadius = val;
        if (val) {
            this._applyVarsDirect('--input-radius-bottom-left', val);
        }
    }
    get bottomLeftRadius() {
        return this._bottomLeftRadius;
    }

    // ---------- Has Object Switcher Bool ---------- \\
    @api
    set isAddon(val) {this._isAddon = publicPropertyToBool(val); }
    get isAddon() {return this._isAddon;}


    // ------------ Large Combobox Bool ------------ \\
    @api
    set isGrow(val) {this._isGrow = publicPropertyToBool(val); }
    get isGrow() {return this._isGrow;}

    @api 
    set dropdownGrow(val) {
        this._dropdownGrow = val;
    }
    get dropdownGrow() {
        return this._dropdownGrow;
    }


    // ----------- Dropdown Above Input ------------ \\
    @api // Make the dropdown appear above the input element instead of below
    set isDropdownUp(val) { this._isDropdownUp = publicPropertyToBool(val); }
    get isDropdownUp() { return this._isDropdownUp; }


    // --------------- Input Ele Icon --------------- \\
    @api  // Replaces 'utility:search' icon
    set inputIcon(val) {
        this._inputIcon = comboboxIconPath(val, true);
    }
    get inputIcon() {return this._inputIcon;}


    // -------------- Combobobox Label -------------- \\
    @api
    set labelTop(val) {
        this._labelTop = getLabelsByLanguage(val) ?? val;
    }
    get labelTop() {
        return this._labelTop;
    }
    @api
    set labelBottom(val) {
        this._labelBottom = getLabelsByLanguage(val) ?? val;
    }
    get labelBottom() {
        return this._labelBottom;
    }
    @api
    set menuTitle(val) {
        this._menuTitle = getLabelsByLanguage(val) ?? val;
    }
    get menuTitle() {
        return this._menuTitle;
    }


    // ----------------- Data Type ------------------- \\
    @api
    set dtype(val) {this._dtype = val ? val : "text"}
    get dtype() {return this._dtype;}



    // ------------ Options Configuration ------------ \\
    filterIndex = 0;
    mainIndex = 0;
    batchSize = 50;
    isNewData = false;
    filteredOptions = [];
    overLoadOptions = [];
    _options;
    _selectedOption;
    _forcedSelection;
    _isReadOnly = false;
    _pendingCurrentValue;

    @api suppressNoMatch = false;

    @api
    set currentValue(value) {
        this._currentValue = typeof value === 'object'
            ? value?.value ?? null
            : value;
            
        this._pendingCurrentValue = value;

        if (value && typeof value === 'object') {
            this._value = {
                label: value.selectedLabel ?? value.label ?? '',
                value: value.value || '',
            };
        }

        this.syncCurrentValueSelection();
        this.applyForcedSelection();
    }
    get currentValue() {
        return this._currentValue;
    }

    @api // List of Options
    set options(val) {
        if (
            this.overLoadOptions && (this.overLoadOptions.length !== val.length ||
            (this.filterIndex && this.overLoadOptions[this.filterIndex] !== val[this.filterIndex]))
        ) {
            // Reset overload overflow combobox trackers
            this.resetLoadMoreOptionsVars(this.overLoadOptions);
        }

        this._hasMeta = (val ?? []).some(opt => META_KEYS.some(mK => !!opt?.[mK]));
            
        if (val.length >= this.batchSize) {
            this.overLoadOptions = val.map(option => {
                const {icon, isMeta, optionClass} = handleOptionProps(option, this._hasMeta);
                    
                return { 
                    ...option, 
                    icon,
                    isMeta,
                    optionClass,
                    id: `option-id-${this.idCnt++}`, 
                    isMeta: Object.keys(option).some(k => k.startsWith('meta')) 
                }
                    
            });
            this._options = this.overLoadOptions.slice(0, this.batchSize);

        } else {
            this._options = val.map(option => {
                const {icon, isMeta, optionClass} = handleOptionProps(option, this._hasMeta);
                
                return { 
                    ...option, 
                    icon, 
                    isMeta,
                    optionClass,
                    id: `option-id-${this.idCnt++}`,
                };
            });
        }

        this.syncCurrentValueSelection();
        this.applyForcedSelection();
    }
    get options() {
        return this._options;
    }
    @api suppressErrorText = false;
    @api allowNull = false;
    // =================================================================== \\


    // ========================= Public Methods ========================== \\
    @api clearMe() {
        this._currentValue = null;
        this._value = {value: '', label: ''};
        this.removeSelected(null, true);
    }
    @api focusMe() {this.template.querySelector('[data-id="inputEle"]').focus();}
    @api setValue(value, label=value, selectedLabel='') {
        this._currentValue = value;
        this._value = {
            value: this.isSelectionVariant ? value : null,
            label: selectedLabel ?? label ?? ''
        };
        if (this.isSelectionVariant) {
            this.selectCurrentValue();
        }
        if (this.checkValidity()) {
            this.removeErrorMe();
        }
    }
    @api getValue() {
        return this.isSelectionVariant
            ? this._selectedOption?.value ?? null
            : this._currentValue ?? null;
    }
    @api errorMe(errMsg=null) {
        const errorMessage = errMsg || 'This field is required.';
        this._errorMessage = errorMessage;
        this._hasError = true;
        this.syncInputValidity(errorMessage);
    }
    @api removeErrorMe() {
        this._hasError = false;
        this._errorMessage = null;
        this.syncInputValidity('');
    }
    @api checkValidity() {
        return !this.isRequired || this.hasRequiredValue();
    }
    @api reportValidity() {
        const isValid = this.checkValidity();

        if (isValid) {
            this.removeErrorMe();
        } else {
            this.errorMe('Enter a value.');
        }

        const input = this.getInputElement();
        if (input?.reportValidity) {
            input.reportValidity();
        }

        return isValid;
    }
    // =================================================================== \\


    // Reset the combobox options trackers
    //@api resetLoadMoreOptionsVars() {}
    resetLoadMoreOptionsVars(onlyIdx=false) {
        this.mainIndex = 0;
        this.filterIndex = 0;

        if (!onlyIdx) {
            this._options = this.overLoadOptions.slice(0, 50).map(opt => opt);
        };
    };

    get dropdownCl() {
        const hideCl = this.suppressNoMatch && !this.currentOptions?.length
            ? ' slds-hide'
            : '';
        
        const dropdownGrowCl = !!this.dropdownGrow
            ? ' slds-dropdown_fit-content'
            : '';

        const base = `slds-dropdown slds-dropdown_length-with-icon-7 slds-dropdown_fluid${hideCl}${dropdownGrowCl}`;
        if (!this.dropdownDirection) {
            return base;
        }

        const directionCl = this.dropdownDirection === 'left' ? 'slds-dropdown_right'
            : this.dropdownDirection === 'right' ? 'slds-dropdown_left'
            : this.dropdownDirection === 'top' ? 'slds-dropdown_top'
            : '';

        return `${base} ${directionCl}`;
    }
    get comboboxContainerCl() {
        return this._selectedOption
            ? 'slds-combobox_container slds-has-selection'
            : 'slds-combobox_container';
    }
    get formElementClass() {
        return this._hasError
            ? 'slds-form-element slds-has-error'
            : 'slds-form-element';
    }
    get formElementCl() {
        if (!this.hasSelectedIcon) {
            return this._formElementCl;
        }       
        const classes = new Set(this._formElementCl.split(/\s+/).filter(Boolean));
        classes.delete('slds-input-has-icon_left');
        classes.delete('slds-input-has-icon_right');
        classes.add('slds-input-has-icon_left-right');
        return [...classes].join(' ');
    }
    get selectedIconContainerCl() {
        return `${this._selectedOption?.icon?.container || ''} slds-combobox__input-entity-icon`;
    }
    get hasSelectedIcon() {
        return !this.suppressSelectedIcon && !!this._selectedOption?.icon;
    }
    get selectedIconHref() {
        return this._selectedOption?.icon?.icon;
    }
    get isSelectedMaterialIcon() {
        return !!this._selectedOption?.icon?.isMaterialIcon;
    }
    get selectedIconPath() {
        return this._selectedOption?.icon?.path;
    }
    get selectedIconViewBox() {
        return this._selectedOption?.icon?.viewBox;
    }
    get selectedLabel() {
        return this._selectedOption?.label || '';
    }

    // Track HTML Elements: <this._dropdownTrigger>, <this._comboOptions>, <this._inputEle>
    renderedCallback() {
        if (this.suppressIcon) {
            this.template.querySelector('.slds-combobox__form-element').classList.replace('slds-input-has-icon_left', 'slds-input-has-icon_right')
        }

        // --------------- Get Tracked Combobox Elements --------------- \\
        // DropdownTrigger (aria-expanded && slds-is-open)
        const dropdownTrigger = this.template.querySelector('.slds-dropdown-trigger');
        if (dropdownTrigger) this._dropdownTrigger = dropdownTrigger;

        // Combobox Options (slds-has-focus && aria-selected)
        const comboOptions = this.template.querySelectorAll('.slds-listbox__option_entity');
        if (comboOptions) this._comboOptions = comboOptions;

        // Input element (aria-activedescendant)
        const inputEle = this.template.querySelector('[data-id="inputEle"]');
        if (inputEle) this._inputEle = inputEle;

        // Get the header element
        if (this.menuTitle && !this._headerOption) {
            const header  = this.template.querySelector('[data-element="groupHeader"]');
            if (header) this._headerOption = header;
        }
        // -------------------------------------------------------------- \\

        // Add custom styles based on public property Booleans
        if (!this.isRendered) {
            // slds-dropdown element
            const dropdown = this.template.querySelector('.slds-dropdown_fluid');

            // Make Dropdown appear above the input element
            if (this.isDropdownUp) dropdown.classList.add('dropdown-up');

            // Make dropdown expand to width of <input> element
            if (this.isGrow) dropdown.classList.add('dropdown-grow');

            this.isRendered = true;
        }

        // Add a "load next batch of options" listener if <overloadOptions>
        if (this.overLoadOptions.length && !this.intersectionObserver) {
            this.intersectionObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.loadMoreOptions();
                    }
                });
            });
            const sentinel = this.template.querySelector('.load-more-sentinel');
            if (sentinel) {
                this.intersectionObserver.observe(sentinel);
            }
        }
    }
    // When user reaches end of combobox load the next batch of options (if Overload Options)
    loadMoreOptions() {
        if (this._value.value) {
            // User is filtering
            const startIdx = this.filterIndex;
            const endIdx = startIdx + this.batchSize;
            const nextBatch = this.filteredOptions.slice(startIdx, endIdx);
            this.filterIndex = endIdx;
            this._options = [...this._options, ...nextBatch];
        } else {
            // User is scrolling through full list
            const startIdx = this.mainIndex;
            const endIdx = startIdx + this.batchSize;
            const nextBatch = this.overLoadOptions.slice(startIdx, endIdx).filter(opt => !this._options.some(p => p.id === opt.id));
            this.mainIndex = endIdx;
            this._options = [...this._options, ...nextBatch];
            //if (isFirst) {
            //    setTimeout(() => {this._comboOptions[this.mainIndex - (this.batchSize -3)].scrollIntoView({behavior: 'smooth', block: 'nearest'});});
            //}
        }
    }

    get showInputErrorIcon() {
        return this.suppressErrorText && this._hasError;
    }

    // ============================ Getters ============================== \\
    // Add a Menu title to the list of options
    get ulEleAttributes()  {
        return this.menuTitle
            ? {role: "group", ariaLabel: this.menuTitle}
            : {role: "presentation", ariaLabel: "None"};
    }
    // Set flag for html-rendering of icons ---++ Update options with the iconContainer/Path
    get isIcons() {
        return this._options.some(opt => opt.hasOwnProperty('icon') || opt.hasOwnProperty('img'));
    }
    get listBoxOptionClass() {
        return this._options.some(opt => opt.hasOwnProperty('icon') || opt.hasOwnProperty('img') )
            ? 'slds-media slds-listbox__option slds-listbox__option_entity slds-listbox__option_has-meta'
            : 'slds-media slds-listbox__option slds-listbox__option_entity slds-listbox__option_has-meta slds-listbox__option_plain';
    }
    // Default is true unles isLabelBottom is true
    get isLabelTop() {
        return !this.isLabelBottom;
    }
    // Filter the options
    get currentOptions() {
        if (this.disableLocalFilter) {
            return [...this._options];
        }

        if (this.overLoadOptions && this.overLoadOptions.length > 0) {
            const isMetas = this.overLoadOptions.some(opt => Object.keys(opt).includes('metaOne'));
            const inputVal = this._value?.value?.toLowerCase() || '';
            const inputLabel = this._value?.label?.toLowerCase() || '';

            if (inputVal) {
                const filtered = this.overLoadOptions
                    .map(opt => {
                        const val = opt.value?.toLowerCase() || '';
                        const label = opt.label?.toLowerCase() || '';
                        let score = 0;
                        if (val === inputVal || label === inputVal) {
                            score += 100;
                        } else if (val.startsWith(inputVal) || label.startsWith(inputVal)) {
                            score += 50;
                        } else if (val.includes(inputVal) || label.includes(inputVal)) {
                            score += 25;
                        }

                        if (isMetas) {
                            Object.keys(opt).forEach(key => {
                                if (!key.startsWith('meta') || opt[`isM${key.slice(1)}Strike`]) return;

                                const meta = opt[key]?.toLowerCase() || '';
                                if (meta === inputVal || meta === inputLabel) {
                                    score += 40;
                                } else if (meta.startsWith(inputVal) || meta.startsWith(inputLabel)) {
                                    score += 20;
                                } else if (meta.includes(inputVal) || meta.includes(inputLabel)) {
                                    score += 10;
                                }

                            });
                        }

                        return { opt, score };
                    })
                    .filter(({ score }) => score > 0)
                    .sort((a, b) => b.score - a.score)
                    .map(({ opt }) => opt)
                    .slice(0, this.filterIndex + this.batchSize);

                return filtered;
            } else {
                return [...this._options];
            }
        }

        return this._value.value
            ? this._options.filter(opt =>
                `${opt.value}`.includes(`${this._value.value.toLowerCase()}`) ||
                `${opt.label}`.includes(this._value.value.toLowerCase()) ||
                `${opt.label.toLowerCase()}`.includes(this._value.label.toLowerCase()) ||

                Object.keys(opt).some(key => // Check if the metas include the searched
                    key.startsWith('meta') && !opt[`isM${key.slice(1)}Strike`] &&
                    (
                        opt[key].toLowerCase().includes(this._value.value) ||
                        opt[key].toLowerCase().includes(`${this._value.value}`) ||
                        opt[key].toLowerCase().includes(`${this._value.label.toLowerCase()}`)
                    )
                )
            )
            : [...this._options];
    }
    // =================================================================== \\


    // ==================== Combobox State Handlers ====================== \\
    _helpIcon = MATERIAL_ICONS.info;
    
    _showSelectedHelp = false;
    handleInputFocus() {
        this._showSelectedHelp = true;
    }

    forceTip(auto=false, hide=false) {
        if (!this.suppressErrorText) {
            return;
        }

        const tooltip = this.template.querySelector('x-tooltip-button');
        
        if (tooltip) {
            if (auto) {
                if (this.showInputErrorIcon) {
                    tooltip.forceTipShow();
                } else {
                    tooltip.forceTipHide();
                }
                return;
            } 

            if (hide) {
                tooltip.forceTipHide();
            } else {
                tooltip.forceTipShow();
            }
        }
        
    }

    _hasError = false;
    _errorMessage;
    getInputElement() {
        return this.template.querySelector('[data-id="inputEle"]');
    }
    hasRequiredValue() {
        const isNotEmpty = (val) => val !== undefined && val !== null && val !== ''

        const value = this.getValue();
        if (this.allowNull) {
            return (!value && !this._value.label) ||
                isNotEmpty(value)
        }
        return isNotEmpty(value);
    }
    syncInputValidity(message='') {
        const input = this.getInputElement();
        if (!input) return;

        if (message) {
            input.setAttribute('aria-describedby', 'error-message-unique-id');
            this.forceTip(true);
        } else {
            input.removeAttribute('aria-describedby');
            this.forceTip(true);
        }

        if (input.setCustomValidity) {
            input.setCustomValidity(message);
        }
    }

    _originalFormCl;
    validateRequiredState() {
        if (!this.isRequired) {
            if (this._hasError && this._originalFormCl) {
                this._formElementCl = this._originalFormCl;
            }
            this.removeErrorMe();
            return true;
        }

        if (this.checkValidity()) {
            if (this._hasError && this._originalFormCl) {
                this._formElementCl = this._originalFormCl;
            }
            this.removeErrorMe();
            return true;
        }
        
        if (this.suppressErrorText) {
            if (!this._hasError) {
                const origCl = this._formElementCl;
                this._originalFormCl = origCl;
            }

            if (!this._formElementCl.includes('slds-input-has-icon')) {
                this._formElementCl = `${this._formElementCl} slds-input-has-icon`;
            } 
            if (!this._formElementCl.includes('slds-input-has-icon_left')) {
                this._formElementCl = this._formElementCl.includes('slds-input-has-icon_right') 
                    ? this._formElementCl.replace('slds-input-has-icon_right', 'slds-input-has-icon_left-right')
                    : `${this._formElementCl} slds-input-has-icon_left`;
            }
        }
        this.errorMe('Enter a value.');
        return false;
    }
    // Close Combobox && focus input
    closeCombobox() {
        this.validateRequiredState();

        this._showSelectedHelp = false;
        const val = this._currentValue;

        if (this._dropdownTrigger) {
            this._dropdownTrigger.classList.remove('slds-is-open');
            this._dropdownTrigger.setAttribute("aria-expanded", "false");

            this._comboOptions.forEach(opt => {
                opt.classList.remove('slds-has-focus');
                opt.setAttribute('aria-selected', 'false');
            });

            this._inputEle.removeAttribute('aria-activedescendant');
        }

        if (!this._selectedOption) {
            this._value = { ...this._value, value: null };
        }

        if (this.isEvent) {
            this.isEvent = false;
            this.dispatchEvent(new CustomEvent('change', {
                detail: val,
                bubbles: true,
                composed: true
            }));
        }
    }
    // Open Combobox
    openCombobox() {
        if (this._selectedOption) return;
        if (this._dropdownTrigger) {
            this._dropdownTrigger.classList.add('slds-is-open');
            this._dropdownTrigger.setAttribute("aria-expanded", "true");
            requestAnimationFrame(() => handleDropdownFlip(this));
        }
    }

    // Open/Close dropdown when dropdown trigger clicked
    handleTriggerClick(event) {
        if (this._selectedOption) return;
        if (this._dropdownTrigger.classList.contains('slds-is-open')) {
            this._dropdownTrigger.classList.remove('slds-is-open');
            this._dropdownTrigger.setAttribute("aria-expanded", "false");
        } else {
            this._dropdownTrigger.classList.add('slds-is-open');
            this._dropdownTrigger.setAttribute("aria-expanded", "true");
            requestAnimationFrame(() => handleDropdownFlip(this));
        }
    }
    // =================================================================== \\


    // =================== Listbox Options Handlers ====================== \\
    // Fake Focus && Remove focus from all others (slds-has-focus && aria-selected="true")
    focusSelectedOption(val) {
        this._comboOptions.forEach(option => {
            if (option.dataset.value === val) {
                option.classList.add('slds-has-focus');
                option.setAttribute("aria-selected", "true");
            } else {
                option.classList.remove('slds-has-focus');
                option.setAttribute("aria-selected", "false");
            }
        })
    }
    // Set "aria-activedescendant" on the inputEle && aria-selected="true" on the option
    handleFauxFocusOption(optionId) {
        try {

            this._inputEle.setAttribute("aria-activedescendant", `${optionId}`);
            let cnt=0;
            this._comboOptions.forEach(option => {
                if (option.id === optionId) {
                    option.setAttribute('aria-selected', 'true');
                    option.classList.add('slds-has-focus');
                    if (cnt === 0 && this._headerOption) {this._headerOption.scrollIntoView({behavior: 'smooth', block: 'nearest'});}
                    else { option.scrollIntoView({behavior: 'smooth', block: 'nearest'}); }
                } else {
                    option.setAttribute('aria-selected', 'false');
                    option.classList.remove('slds-has-focus');
                }
                cnt++;
            });
        }catch(error) {
            console.error('handleFauxFocusOption', error);
        }
    }
    // Don't let focus leave the input element
    deflectFocus(event)  {
        event.preventDefault();
    }
    // Set the value to the clicked options value
    handleOptionClick(event) {
        this._currentValue = event.currentTarget.dataset.value;
        this.removeErrorMe();
        const label = event.currentTarget.dataset.label;
        const selectedLabel = event.currentTarget.dataset.selectedLabel
            ?? label ?? '';

        this._value = {
            value: event.currentTarget.dataset.value,
            label: selectedLabel ?? label,
        };
        if (this.isSelectionVariant) {
            this.selectCurrentValue();
        }
        this.isEvent = true;
        setTimeout(() => {
            this.closeCombobox();
        }, 20);
    }
    // =================================================================== \\
    

    findOption(value, label='') {
        const optionSets = [this._options, this.overLoadOptions]
            .filter(Array.isArray);

        if (Array.isArray(this._options)) {
            optionSets.push(this.currentOptions);
        }

        for (const optionSet of optionSets) {
            const selected = optionSet.find(option =>
                option?.value === value || (!!label && option?.label === label)
            );
            if (selected) {
                return selected;
            }
        }

        return null;
    }

    normalizeSelection(selection) {
        if (selection === undefined || selection === null || selection === '') return null;

        if (typeof selection === 'object') {
            const value = selection.value ?? '';
            const label = selection.label ?? value;
            if (value === '' && label === '') return null;
            return {
                ...selection,
                value,
                label
            };
        }

        return {
            value: selection,
            label: selection
        };
    }

    hasSelectionContent(selection) {
        return (selection?.value !== undefined && selection?.value !== null && selection?.value !== '') ||
            (selection?.label !== undefined && selection?.label !== null && selection?.label !== '');
    }

    isSameSelection(firstSelection, secondSelection) {
        const first = this.normalizeSelection(firstSelection);
        const second = this.normalizeSelection(secondSelection);

        if (!first || !second) return false;

        return (first.value !== '' && first.value === second.value) ||
            (first.label !== '' && first.label === second.label);
    }

    isForcedSelectionSelected() {
        return this.isSelectionVariant &&
            this._forcedSelection &&
            this._selectedOption &&
            this.isSameSelection(this._selectedOption, this._forcedSelection);
    }

    clearSelectionState() {
        this._selectedOption = null;
        this._showSelectedHelp = false;
        this._isReadOnly = false;
        this._currentValue = null;
        this._value = {value: '', label: ''};
    }

    applyForcedSelection() {
        if (!this.isSelectionVariant || !this._forcedSelection) return;

        const forced = this.normalizeSelection(this._forcedSelection);
        if (!forced) return;

        const selected = this.findOption(forced.value, forced.label) || forced;

        this.setSelectedOption(selected, true);
    }

    setSelectedOption(selected, isForced=false) {
        if (!this.hasSelectionContent(selected)) return;

        this._selectedOption = {
            label: selected.selectedLabel ?? selected.label ?? '',
            value: selected.value ?? '',
            icon: typeof selected.icon === 'string'
                ? comboboxIconPath(selected.icon)
                : selected.icon || null
        };

        this._currentValue = this._selectedOption.value;
        this.removeErrorMe();

        this._value = {
            label: this._selectedOption.selectedLabel ?? this._selectedOption.label,
            value: this._selectedOption.value,
        };
        this._isReadOnly = true;

        if (!isForced) { 
            requestAnimationFrame(() => {
                this._showSelectedHelp = true;
            })
        }
    }

    selectCurrentValue() {
        if (!this.isSelectionVariant) return;

        const selected = this.findOption(this._value?.value, this._value?.label) || {
            label: this._value?.label,
            value: this._value?.value
        };

        if (!this.hasSelectionContent(selected)) return;

        this.setSelectedOption(selected);
    }

    syncCurrentValueSelection() {
        if (!this._pendingCurrentValue) {
            return;
        }

        const value = typeof this._pendingCurrentValue === 'object'
            ? this._pendingCurrentValue?.value
            : this._pendingCurrentValue;

        const label = typeof this._pendingCurrentValue === 'object'
            ? this._pendingCurrentValue?.label
            : '';

        if (value === undefined || value === null || value === '') {
            this._currentValue = null;
            this._value = {value: '', label: ''};
            this._selectedOption = null;
            this._isReadOnly = false;
            this._pendingCurrentValue = undefined;
            return;
        }

        const selected = this.findOption(value, label);
        if (!selected && typeof this._pendingCurrentValue !== 'object' && this.isSelectionVariant) return;

        this._value = {
            value: this.isSelectionVariant ? value : null,
            label: selected?.selectedLabel ?? selected?.label ?? label ?? value,
        };
        this._currentValue = value;

        if (this.isSelectionVariant) {
            this._value = {
                ...this._value,
                value,
            };
            this.selectCurrentValue();
        }

        this._pendingCurrentValue = undefined;
    }

    removeSelected(event=null, suppressEvent=false) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        const removedSelection = this._selectedOption;
        const removedForcedSelection = this.isForcedSelectionSelected();
        const shouldDispatchForcedSelectionRemoved = !!event && !suppressEvent && removedForcedSelection;

        this.clearSelectionState();

        if (!suppressEvent) {
            this.dispatchEvent(new CustomEvent('change', {
                detail: null,
                bubbles: true,
                composed: true
            }));
        }

        if (removedForcedSelection) {
            this._forcedSelection = null;
        }

        if (shouldDispatchForcedSelectionRemoved) {
            this.dispatchEvent(new CustomEvent('forcedselectionremoved', {
                detail: removedSelection,
                bubbles: true,
                composed: true
            }));
        }

        requestAnimationFrame(() => this.focusMe());
    }

    handleRemoveKeydown(event) {
        if (event.key === 'Enter' || event.key === 'Backspace' || event.key === 'Escape') {
            suppressEscape(event);
            this.removeSelected(event);
        }
    }   

    handleChange(event) {
        const value = event.target.value;
        console.log('value in handleChange() in sldsComboboxSearch.js: ', value);
        this._value = {
            value: value,
            label: value
        };
        
        if (!value && this._hasError && this.allowNull && this.suppressErrorText) {
            this._hasError = false;
        }
    }

    // Detect current <input> element entry --> Open combobox  if not open
    handleInput(event) {
        const value = event.target.value;
        if (!value && this._hasError && this.allowNull && this.suppressErrorText) {
            this._hasError = false;
        }
        if (this._selectedOption) {
            return;
        }
        if (!this._dropdownTrigger.classList.contains('slds-is-open')) {
            this.openCombobox();
        }
        this._currentValue = null;
        this._value = {
            value: event.target.value,
            label: event.target.value,
        };
        this.dispatchEvent(new CustomEvent('inputchange', {
            detail: event.target.value,
            bubbles: true,
            composed: true
        }));
    }
    // Close the combobox Tab/Escape --> Open if Enter
    handleKeyDown(event) {
        if (this._selectedOption) {
            this.handleRemoveKeydown(event);
            return;
        }

        // Close combobox on 'Escape'/'Tab'
        if (event.key === 'Tab' || event.key === 'Escape') {
            suppressEscape(event);
            this.closeCombobox();
            return;
        }

        // Open/Close combobox on 'Enter'
        if (event.key === 'Enter') {
            if (this._dropdownTrigger.classList.contains('slds-is-open')) {
                const selected = Array.from(this._comboOptions).find(opt => opt.classList.contains('slds-has-focus'));
                if (selected) {
                    this.isEvent = true;
                    this._currentValue = selected.dataset.value;
                    this._value = {
                        value: selected.dataset.value,
                        label: selected.dataset.selectedLabel ?? 
                            selected.dataset.label ?? 
                            selected.dataset.label,
                    };
                    if (this.isSelectionVariant) {
                        this.selectCurrentValue();
                    }
                }
                this.closeCombobox();
                return;
            }
            this.openCombobox();
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            const isDown = event.key === 'ArrowDown';
            const arrLength = Array.from(this._comboOptions).length;

            const currentSelected = Array.from(this._comboOptions).findIndex(opt =>
                opt.getAttribute('aria-selected') === 'true'
            );

            let nextIdx = 0

            if (isDown) {
                nextIdx = currentSelected===-1 || currentSelected + 1 > arrLength -1
                    ? 0
                    : currentSelected + 1
            } else {
                nextIdx = currentSelected===-1 || currentSelected - 1 < 0
                    ? arrLength - 1
                    : currentSelected - 1
            }
            this.handleFauxFocusOption(this._comboOptions[nextIdx].id);
        }

    }

    // Apply Styles to the host 👉 Can pass 1.Array of Arrays   2.Array of Object   3.Strings
    _applyVarsDirect(cssVar, varStyle=null, eleAttr=null) {

        if (eleAttr) {
            const ele = this.template.querySelector(cssVar);
            if (ele) {
                ele.style[eleAttr] = varStyle;
            }
            return;
        }

        // Check that CSS var starts with '--' and has a non-null stlye
        const isValid = (cssVarP, varStyleP) => {
            return typeof cssVarP === 'string' &&
                typeof varStyleP === 'string' &&
                cssVarP?.startsWith('--') &&
                varStyleP;
        }


        if (Array.isArray(cssVar)) {
            // Handle Array of vars
            cssVar.forEach(hostVar => {
                if (Array.isArray(hostVar) && isValid(hostVar[0], hostVar[1])) {
                    // Handle Array structure  (CSS var at index 0)
                    this.style.setProperty(hostVar[0], hostVar[1]);
                } else if (typeof hostVar === 'object' && isValid(hostVar.cssVar, hostVar.varStyle)) {
                    // Handle Object structure (cssVar & varStyle keys)
                    this.style.setProperty(hostVar.cssVar, hostVar.varStyle)
                }
            });
        } else if (isValid(cssVar, varStyle)) {
            // Handle string arguments
            this.style.setProperty(cssVar,  varStyle);
        }
    }
}
